import { Link } from "react-router-dom";
import { ArrowRight, HandHeart } from "lucide-react";
import { usePageMeta } from "../hooks/usePageMeta";
import { CtaBand } from "../components/CtaBand";
import { SERVICES, PROCESS_STEPS } from "../data/site";

const DETAILS = {
  "estate-sales": {
    overview:
      "A MacWay estate sale is a complete service: we sort, stage, price, advertise, and host the sale, then help you decide what happens to what's left. You stay in control of the decisions; we carry the workload.",
    situations: ["Settling a loved one's estate", "Emptying a home before it goes on the market", "Liquidating a household before a move", "Managing an estate from out of the area"],
    link: { to: "/services/estate-sales", label: "Explore estate sales" },
  },
  "estate-cleanouts": {
    overview:
      "Whether a sale just ended or the house simply needs to be emptied, our cleanout crews remove everything you don't want kept — donations, recyclables, and disposal all handled — and leave the property broom-clean.",
    situations: ["After an estate sale wraps up", "Preparing a property for sale or rent", "Clearing a home after a move", "Handling a property on a deadline"],
    link: { to: "/estate-cleanouts", label: "Get a cleanout estimate" },
  },
  downsizing: {
    overview:
      "Downsizing is as much about decisions as it is about boxes. We work at your pace to sort belongings into keep, sell, donate, and let-go — then handle the selling, donating, and moving so the plan actually happens.",
    situations: ["Moving to a smaller home or senior living", "Helping a parent transition", "Combining two households", "Simply ready for less"],
    link: { to: "/services/cleanouts-downsizing", label: "Explore cleanouts & downsizing" },
  },
  "packing-moving": {
    overview:
      "Careful, methodical packing with everything labeled and inventoried. We can coordinate with your movers, manage move day, and help set up the new home so it feels like home from the first night.",
    situations: ["Packing up a longtime family home", "Coordinating a senior's move", "Partial packing for fragile or valuable items", "Unpacking and settling in at the new place"],
  },
  "donation-pickup": {
    overview:
      "This is a paid pickup and removal service — not a free charity pickup. We provide convenient pickup and removal of unwanted furniture, household items, and donations for a service fee, priced by the amount of items, the labor required, and the pickup location. Whenever possible, donatable items are matched with local charities so useful goods find a second home instead of a landfill.",
    situations: ["Leftover items after a sale", "Furniture that deserves a second life", "Clearing space without lifting or hauling yourself", "Unwanted items you want handled responsibly"],
  },
  decluttering: {
    overview:
      "Room by room, we help you decide what stays and set up simple systems that keep it that way. Practical, patient, and never pushy — it's your home and your call.",
    situations: ["Getting a home ready to list", "Garages, attics, and basements that got away from you", "Paperwork and memorabilia overload", "A fresh start after a life change"],
  },
  "junk-removal": {
    overview:
      "From a single bulky item to a whole property, we haul away what you no longer want — responsibly, with recycling and donation considered before disposal.",
    situations: ["Old furniture and appliances", "Post-cleanout odds and ends", "Yard and garage debris", "The things the city won't take"],
  },
  "hoarding-assistance": {
    overview:
      "Hoarding situations call for patience, not judgment. We work alongside you and your family at a pace that feels safe, with a clear plan and complete discretion. Progress, not pressure.",
    situations: ["Helping a family member you're worried about", "Making a home safe and livable again", "Working alongside counselors or social workers", "Taking the first small step"],
  },
};

export default function ServicesPage() {
  usePageMeta(
    "Our Services | MacWay Estate Services — DC, MD & VA",
    "Estate sales, estate cleanouts, downsizing, packing & moving assistance, donation pickups, decluttering & home organization, junk removal, and judgment-free hoarding assistance across DC, Maryland, and Virginia."
  );

  return (
    <main data-testid="services-page">
      <section className="bg-forest text-ivory py-20 md:py-28" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl">
          <p className="eyebrow-label">Our services</p>
          <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight" data-testid="services-headline">
            Everything the house needs, under one roof
          </h1>
          <p className="mt-6 text-lg text-ivory/80 leading-relaxed max-w-2xl">
            Every engagement starts with a walkthrough and a written quote. Mix and match services — most of our
            projects combine two or three.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-20">
          {SERVICES.map((s, i) => {
            const d = DETAILS[s.slug];
            const flip = i % 2 === 1;
            return (
              <article
                key={s.slug}
                id={s.slug}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center scroll-mt-32`}
                data-testid={`service-section-${s.slug}`}
              >
                <div className={flip ? "lg:order-2" : ""}>
                  {s.image ? (
                    <img src={s.image} alt={s.name} loading="lazy" className="w-full h-80 object-cover border border-linen" />
                  ) : (
                    <div className="w-full h-80 bg-forest flex items-center justify-center">
                      {s.slug === "junk-removal" ? (
                        <svg className="w-16 h-16 text-brass" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/></svg>
                      ) : (
                        <HandHeart className="w-16 h-16 text-brass" />
                      )}
                    </div>
                  )}
                </div>
                <div className={flip ? "lg:order-1" : ""}>
                  <p className="eyebrow-label">{String(i + 1).padStart(2, "0")}</p>
                  <h2 className="mt-3 text-3xl sm:text-4xl font-medium tracking-tight text-ink">{s.name}</h2>
                  {s.badge && (
                    <span
                      className="mt-3 inline-flex self-start items-center bg-brass/15 border border-brass/50 text-brass-hover px-3 py-1 text-[0.7rem] font-bold uppercase tracking-[0.14em]"
                      data-testid={`service-badge-${s.slug}`}
                    >
                      {s.badge}
                    </span>
                  )}
                  <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">{d.overview}</p>
                  <div className="mt-6">
                    <h3 className="font-sans text-sm font-bold uppercase tracking-[0.16em] text-forest">Common situations</h3>
                    <ul className="mt-3 space-y-2">
                      {d.situations.map((sit) => (
                        <li key={sit} className="flex items-start gap-3 text-sm text-ink/70">
                          <span className="mt-1.5 w-1.5 h-1.5 bg-brass shrink-0" aria-hidden="true" />
                          {sit}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="mt-7 flex flex-wrap items-center gap-4">
                    {d.link && (
                      <Link to={d.link.to} className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-forest hover:text-brass transition-colors" data-testid={`service-detail-link-${s.slug}`}>
                        {d.link.label} <ArrowRight className="w-4 h-4" />
                      </Link>
                    )}
                    <Link to="/contact" className="btn-outline-dark text-xs px-5 py-2.5" data-testid={`service-quote-cta-${s.slug}`}>
                      Request a Quote
                    </Link>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="py-16 md:py-24 bg-sand" data-testid="services-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">How every project runs</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">The same simple process, whatever the job</h2>
          </div>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {PROCESS_STEPS.map((p) => (
              <div key={p.step} className="bg-white border border-linen p-8">
                <span className="font-serif text-5xl font-semibold text-brass">{p.step}</span>
                <h3 className="mt-4 text-2xl font-medium text-ink">{p.title}</h3>
                <p className="mt-3 text-base text-ink/70 leading-relaxed">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CtaBand />
    </main>
  );
}
